import { Component, Inject, OnInit } from '@angular/core';
import { NoteDTO } from '@iota/iota-management-portal-v4-client';
import { IotaModalOptions, IotaModalSize, MAT_DIALOG_DATA, MatDialogRef, ModalService } from '@iota/iota-ui-components';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { AuditInvoiceSummaryEntity } from 'src/app/store/entity-models';
import { auditInvoiceSummaryById } from 'src/app/store/store';
import { getValueFromStore, waitForBooleanValue } from '../../utils';
import {
    ApproveAuditInvoiceSummary,
    CreateAuditInvoiceSummaryNote,
    RejectAuditInvoiceSummary,
    RemoveAuditInvoiceSummaryNote
} from 'src/app/store/actions/audit-invoice-summary.actions';
import { ConfirmationDialogData, DialogConfirmationComponent } from '../dialog-confirmation/dialog-confirmation.component';
import { auditInvoiceSummaryActionError, auditInvoiceSummaryActionLoading } from 'src/app/store/reducer/audit-invoice-summary.reducer';

export enum InvoiceReviewDialogType {
    SHOW_NOTES = 'SHOW_NOTES',
    APPROVE_INVOICE = 'APPROVE_INVOICE',
    REJECT_INVOICE = 'REJECT_INVOICE',
    SHOW_ERROR = 'SHOW_ERROR'
}
export interface InvoiceReviewDialogData {
    auditInvoiceSummaryId: string;
    dialogType: InvoiceReviewDialogType;
}

@Component({
    standalone: false,
    selector: 'app-invoice-review-dialog',
    templateUrl: './invoice-review-dialog.component.html',
    styleUrls: ['./invoice-review-dialog.component.scss']
})
export class InvoiceReviewDialogComponent implements OnInit {
    auditInvoiceSummary$: Observable<AuditInvoiceSummaryEntity>;
    actionError$ = this.store.select(auditInvoiceSummaryActionError);
    actionLoading$ = this.store.select(auditInvoiceSummaryActionLoading);

    constructor(
        private dialog: ModalService,
        public dialogRef: MatDialogRef<InvoiceReviewDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: InvoiceReviewDialogData,
        private store: Store
    ) {}

    ngOnInit(): void {
        this.auditInvoiceSummary$ = this.store.select(auditInvoiceSummaryById(this.data.auditInvoiceSummaryId));
    }

    onCancelClick(): void {
        this.dialogRef.close();
    }

    get isShowNotesDialog() {
        return this.data.dialogType === InvoiceReviewDialogType.SHOW_NOTES;
    }
    get isApproveInvoiceDialog() {
        return this.data.dialogType === InvoiceReviewDialogType.APPROVE_INVOICE;
    }
    get isRejectInvoiceDialog() {
        return this.data.dialogType === InvoiceReviewDialogType.REJECT_INVOICE;
    }
    get isShowErrorDialog() {
        return this.data.dialogType === InvoiceReviewDialogType.SHOW_ERROR;
    }

    get dialogTitle() {
        if (this.isShowNotesDialog) return $localize`Invoice Comments`;
        if (this.isShowErrorDialog) return $localize`Invoice Error`;
        if (this.isApproveInvoiceDialog) return $localize`Approve Invoice`;
        if (this.isRejectInvoiceDialog) return $localize`Reject Invoice`;
    }

    get modalOptions(): Partial<IotaModalOptions> {
        if (this.isShowNotesDialog) return { showToggleFullScreenButton: true, size: IotaModalSize.SIZE_3XL };
        if (this.isShowErrorDialog) return { showToggleFullScreenButton: true, size: IotaModalSize.SIZE_7XL };
        if (this.isApproveInvoiceDialog) return { showToggleFullScreenButton: false, size: IotaModalSize.SMALL };
        if (this.isRejectInvoiceDialog) return { showToggleFullScreenButton: false, size: IotaModalSize.SMALL };
    }

    addNote(note: NoteDTO) {
        if (note && note.note.trim().length > 0) {
            this.store.dispatch(CreateAuditInvoiceSummaryNote({ auditInvoiceSummaryId: this.data.auditInvoiceSummaryId, note }));
        }
    }

    removeNote(note: NoteDTO) {
        const dialogData: ConfirmationDialogData = {
            title: $localize`Delete comment`,
            text: $localize`Are you sure you want to delete the comment?`,
            confirmButtonStyle: 'error'
        };

        this.dialog
            .open(DialogConfirmationComponent, {
                data: dialogData
            })
            .afterClosed()
            .subscribe(async (result) => {
                if (result) {
                    const auditInvoiceSummary = await getValueFromStore(
                        this.store,
                        auditInvoiceSummaryById(this.data.auditInvoiceSummaryId)
                    );

                    this.store.dispatch(
                        RemoveAuditInvoiceSummaryNote({
                            auditInvoiceSummaryId: auditInvoiceSummary.auditInvoiceSummaryId,
                            noteId: note.noteId
                        })
                    );
                }
            });
    }

    rejectInvoice() {
        this.store.dispatch(RejectAuditInvoiceSummary({ auditInvoiceSummaryId: this.data.auditInvoiceSummaryId }));
        this.closeIfActionSuccessful();
    }

    approveInvoice() {
        this.store.dispatch(ApproveAuditInvoiceSummary({ auditInvoiceSummaryId: this.data.auditInvoiceSummaryId }));
        this.closeIfActionSuccessful();
    }

    async closeIfActionSuccessful() {
        await waitForBooleanValue(this.store, auditInvoiceSummaryActionLoading, false);
        const error = await getValueFromStore(this.store, auditInvoiceSummaryActionError);
        if (!error) this.dialogRef.close();
    }
}
